import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { clubBillingSchema, type ClubBillingDto, type ClubBillingInput } from "@tc/shared";
import { Button, Card, Field, TextInput, Toast } from "@/components/ui";

interface FormState {
  companyName: string;
  taxId: string;
  street: string;
  postalCode: string;
  city: string;
  province: string;
  accountingEmail: string;
}

const EMPTY: FormState = {
  companyName: "",
  taxId: "",
  street: "",
  postalCode: "",
  city: "",
  province: "",
  accountingEmail: "",
};

function toForm(dto: ClubBillingDto): FormState {
  return {
    companyName: dto.companyName,
    taxId: dto.taxId,
    street: dto.street,
    postalCode: dto.postalCode,
    city: dto.city,
    province: dto.province,
    accountingEmail: dto.accountingEmail,
  };
}

/**
 * The club billing-details form (TW-3527) — three cards (Invoice details / Billing address /
 * Accounting) matching the design. Reused by the club's Settings → Billing section and the
 * back-office club admin, parameterised only by how it loads/saves: the club self-service endpoints
 * (`/api/billing`) or the back-office by-club endpoints (`/api/clubs/:id/billing`). Saving the legal
 * identity appends a new billing version server-side; the accounting email is updated in place. The
 * single Zod schema drives validation, so the form and the API agree on what's valid.
 */
export function BillingForm({
  load,
  save,
}: {
  load: () => Promise<ClubBillingDto>;
  save: (input: ClubBillingInput) => Promise<ClubBillingDto>;
}) {
  const { t } = useTranslation();
  const [initial, setInitial] = useState<FormState | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    load()
      .then((dto) => {
        if (!alive) return;
        const next = toForm(dto);
        setInitial(next);
        setForm(next);
      })
      .catch(() => alive && setLoadError(true));
    return () => {
      alive = false;
    };
  }, [load]);

  const isDirty = useMemo(() => (initial ? dirty(form, initial) : false), [form, initial]);

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  }

  function set<K extends keyof FormState>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  async function onSave() {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.companyName.trim()) next.companyName = t("settings.billing.required");
    if (!form.taxId.trim()) next.taxId = t("settings.billing.required");
    if (!form.accountingEmail.trim()) next.accountingEmail = t("settings.billing.required");

    const parsed = clubBillingSchema.safeParse(form);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof FormState;
        if (field && !next[field]) next[field] = issue.message;
      }
    }
    if (Object.keys(next).length > 0 || !parsed.success) {
      setErrors(next);
      return;
    }

    setSaving(true);
    try {
      const saved = await save(parsed.data);
      const synced = toForm(saved);
      setInitial(synced);
      setForm(synced);
      setErrors({});
      flash(t("settings.billing.saved"));
    } catch {
      flash(t("settings.billing.saveError"));
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    if (initial) setForm(initial);
    setErrors({});
  }

  if (loadError) {
    return <p className="muted">{t("settings.billing.loadError")}</p>;
  }
  if (!initial) {
    return <p className="muted">{t("common.loading")}</p>;
  }

  return (
    <div className="billing-form">
      <Card>
        <div className="set-section-head">
          <h2>{t("settings.billing.invoiceDetails.title")}</h2>
          <div className="sub">{t("settings.billing.invoiceDetails.sub")}</div>
        </div>
        <div className="form-grid" style={{ marginTop: 18 }}>
          <Field label={t("settings.billing.companyName")} required error={errors.companyName} full>
            <TextInput icon="building" value={form.companyName} onChange={(v) => set("companyName", v)} />
          </Field>
          <Field
            label={t("settings.billing.taxId")}
            required
            error={errors.taxId}
            hint={t("settings.billing.taxIdHint")}
            full
          >
            <TextInput value={form.taxId} onChange={(v) => set("taxId", v)} />
          </Field>
        </div>
      </Card>

      <Card style={{ marginTop: 18 }}>
        <div className="set-section-head">
          <h2>{t("settings.billing.address.title")}</h2>
        </div>
        <div className="form-grid" style={{ marginTop: 18 }}>
          <Field label={t("settings.billing.street")} error={errors.street} full>
            <TextInput value={form.street} onChange={(v) => set("street", v)} />
          </Field>
          <Field label={t("settings.billing.postalCode")} error={errors.postalCode}>
            <TextInput value={form.postalCode} onChange={(v) => set("postalCode", v)} />
          </Field>
          <Field label={t("settings.billing.city")} error={errors.city}>
            <TextInput value={form.city} onChange={(v) => set("city", v)} />
          </Field>
          <Field label={t("settings.billing.province")} error={errors.province} full>
            <TextInput value={form.province} onChange={(v) => set("province", v)} />
          </Field>
        </div>
      </Card>

      <Card style={{ marginTop: 18 }}>
        <div className="set-section-head">
          <h2>{t("settings.billing.accounting.title")}</h2>
          <div className="sub">{t("settings.billing.accounting.sub")}</div>
        </div>
        <div className="form-grid" style={{ marginTop: 18 }}>
          <Field
            label={t("settings.billing.accountingEmail")}
            required
            error={errors.accountingEmail}
            hint={t("settings.billing.accountingEmailHint")}
            full
          >
            <TextInput
              icon="mail"
              type="email"
              value={form.accountingEmail}
              onChange={(v) => set("accountingEmail", v)}
            />
          </Field>
        </div>

        <div className="card-foot">
          {isDirty && (
            <span className="foot-note">
              <span className="badge-dot" style={{ background: "var(--warning)" }} />
              {t("settings.billing.unsaved")}
            </span>
          )}
          <Button variant="ghost" onClick={cancel} disabled={!isDirty || saving}>
            {t("actions.cancel")}
          </Button>
          <Button variant="primary" icon="check" onClick={onSave} disabled={!isDirty || saving}>
            {t("settings.billing.save")}
          </Button>
        </div>
      </Card>

      {toast && <Toast message={toast} />}
    </div>
  );
}

/** Shallow trimmed compare driving the dirty/unsaved state. */
function dirty(a: FormState, b: FormState): boolean {
  return (Object.keys(a) as (keyof FormState)[]).some((k) => a[k].trim() !== b[k].trim());
}
